import { storeToRefs } from "pinia";
import { computed } from "vue";
import { BillingDetailsForm } from "../types/types";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";

// Billing details belong to the paying organization, not to a project — the key is
// scoped to the tenant so two organizations on the same browser don't see each
// other's invoicing data prefilled.
const storageKey = (tenantId: string) => `gaiup-${tenantId}-billing-details`;

const isBillingDetailsForm = (value: unknown): value is BillingDetailsForm =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const useBillingDetails = () => {
  const authStore = useAuthStateStore();
  const { user } = storeToRefs(authStore);

  const tenantId = computed(() => user.value?.tenantId ?? "");

  const readBillingDetails = (): BillingDetailsForm => {
    if (!tenantId.value) return {};

    try {
      const stored = localStorage.getItem(storageKey(tenantId.value));
      if (!stored) return {};
      const parsed: unknown = JSON.parse(stored);
      return isBillingDetailsForm(parsed) ? parsed : {};
    } catch {
      // corrupted data — fall back to an empty form
      return {};
    }
  };

  const writeBillingDetails = (billing: BillingDetailsForm) => {
    if (!tenantId.value) return;

    try {
      localStorage.setItem(storageKey(tenantId.value), JSON.stringify(billing));
    } catch {
      // intentional: prefilling next time is a convenience, a full quota is not
      // a reason to interrupt a purchase
    }
  };

  return {
    // actions
    readBillingDetails,
    writeBillingDetails,
  };
};
